import get from 'lodash/get';
import _ from 'underscore';

import RetailManager from 'app/retail_manager/RetailManager';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import CommonData from 'app/src/data/CommonData';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Payment from 'app/src/data/models/Payment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import Localize from 'app/src/localize/Localize';
import CardOnFilePayment from 'app/src/payments/CardOnFilePayment';
import DepositManager from 'app/src/payments/DepositManager';
import DepositPayment from 'app/src/payments/DepositPayment';
import GiftCardPayment from 'app/src/payments/GiftCardPayment';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleManager from 'app/src/payments/SaleManager';
import SwipePayment from 'app/src/payments/SwipePayment';
import TerminalPayment from 'app/src/payments/TerminalPayment';
import PaymentListView from 'app/src/views/payments/PaymentListView';
import { createReactRenderer } from 'helpers/react-renderer';
import TransactionResultModal from 'payments/components/TransactionResultModal';
import TerminalPaymentModal from 'payments/TerminalPayment';
import { resolvePayment } from 'root/apps/app/src/payments/payservice-helpers';
import InvoiceServiceLegacy from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import RefundIntentService, {
  buildCustomerFromData,
  buildEmployeeFromData,
  buildLocationFromShopData,
} from 'root/apps/QuoteMachineIntegration/services/v1/RefundIntentService';
import { getEmployeeEmail } from 'root/apps/QuoteMachineIntegration/utils/Employee';
import { datadogRum } from 'root/datadog/datadogRum';
import Modules from 'shared_modules/Modules';
import Monitoring from 'shared_modules/Monitoring';

const supportMessage = `Try again and if the problem persists, contact <a class="strong-link" target="_blank" href="https://www.lightspeedhq.com/support/retail">Support</a>`;

export default class PaymentListViewController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    if (!(paymentProvider instanceof PaymentProvider)) {
      throw 'A payment provider must be provided.';
    }

    super(saleManager);

    this._paymentProvider = paymentProvider;

    // payments that may be listed include bank and gift card payments
    this._saleManager.paymentProvider = paymentProvider;
    this._salePayments = this._saleManager.creditDebitPayments;
    this._salePayments.add(this._saleManager.giftCardPayments.models); // merge the collections

    this._addDepositRefunds();

    let hideRemoveButton = false;
    if (
      this._paymentProvider.activeGateway &&
      this._paymentProvider.activeGateway.get('gatewayID') === GatewayConfig.GatewayType.TYRO
    ) {
      hideRemoveButton = true;
    }

    this.view = new PaymentListView({
      controller: this,
      collection: this._salePayments,
      isRefundSale: this._saleManager.isRefundSale(),
      hideRemoveButton,
      disableRemoveButton: !this._paymentProvider.activeGateway,
    });
  }

  /**
   * Shows the view managed by the controller.
   */
  show() {
    super.show();

    this.listenTo(
      RetailManager.events,
      RetailManager.Event.REFUND_COMPLETE,
      this._onRefundComplete
    );
    this.listenTo(this._saleManager, SaleManager.Events.SALE_REFRESHED, this._refreshPayments);
    this.listenTo(
      this._saleManager.deposits,
      DepositManager.Events.DEPOSITS_CHANGED,
      this._addDepositRefunds
    );
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_STARTED,
      this._onCompleteSaleStarted
    );
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_FAILED,
      this._onCompleteSaleFailed
    );
  }

  /**
   * Event Handler for when the resolve button is click from within the list.
   *
   * @param paymentView
   */
  async onPaymentResolveButtonClicked(paymentView) {
    try {
      let payment = PaymentFactory.createPaymentFromModel(paymentView.model);
      const paymentId = payment.model.get('paymentID');
      const updatedPayment = await resolvePayment(paymentId);

      const paymentIntentId = get(
        updatedPayment,
        'attributes.cardPaymentRequest.parameters.paymentIntentId',
        ''
      );
      const transactions = get(updatedPayment, 'relationships.transactions.data', []);

      // Step1: Check if paymentIntent exists
      // Step2: Check if transactions exists,
      // it not, we need to collect a payment method to be charged
      if (!paymentIntentId || !transactions.length) {
        const terminal = this.getTerminal();
        if (
          this._paymentProvider.isGatewayLightspeedPayments &&
          terminal &&
          terminal.hasMonitoringModule
        ) {
          terminal.startLspayMonitoring(this._paymentProvider.activeGatewayPli, {
            payment: { id: paymentId },
            paymentIntentId,
            transactions,
            isResolve: true,
          });
        }

        return TerminalPaymentModal.doTerminalPayment({
          initialAmount: payment.model.get('amount'),
          saleManager: this._saleManager,
          paymentProvider: this._paymentProvider,
          recoveredPayment: this._saleManager.getServicePayment(payment.model.get('paymentID')),
          ...(terminal && { terminal }),
        });
      }

      this._saleManager.refreshSale();
    } catch (error) {
      Monitoring.addError(
        new Error(
          `Merchant received an error while attempting to resolve a payment from the sale.`
        ),
        'PaymentListViewController',
        {
          reason: error,
        }
      );
      alertUser(Localize.translate(`Unable to resolve payment. ${supportMessage}`));

      throw error;
    }
  }

  /**
   * Event Handler for when the cancel button is click from within the list.
   *
   * @param paymentView
   */
  async onPaymentCancelButtonClicked(paymentView) {
    try {
      let payment = PaymentFactory.createPaymentFromModel(paymentView.model);

      payment.serviceModel.set('paymentID', payment.model.get('paymentID'));

      await payment.serviceModel.fetch();
      payment.serviceModel.cancelReason = Payment.CancelReasons.REQUESTED_BY_CUSTOMER;

      await payment.serviceModel.cancelPayment();

      this._saleManager.refreshSale();
    } catch (error) {
      Monitoring.addError(
        new Error(`Merchant received an error while attempting to cancel a payment from the sale.`),
        'PaymentListViewController',
        {
          reason: error,
        }
      );
      alertUser(Localize.translate(`Unable to cancel payment. ${supportMessage}`));

      throw error;
    }
  }

  /**
   * Event handler for when a remove button is clicked from within the list.
   *
   * @param {PaymentListPaymentView} paymentView  The view of the payment which triggered the event.
   */
  onPaymentRemoveButtonClicked(paymentView) {
    let payment = null;
    if (paymentView.model.depositPayment) {
      // re-build the deposit payment
      payment = PaymentFactory.createPayment(PaymentTypeManager.Types.DEPOSIT);
      payment.model = paymentView.model;
      payment.depositPayment = paymentView.model.depositPayment;
    } else {
      payment = PaymentFactory.createPaymentFromModel(paymentView.model);
    }

    if (payment instanceof TerminalPayment) {
      PaymentAnalytics.trackPaymentEvent('Remove', this._saleManager.sale.id, {
        salePaymentID: payment.model.id,
        paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
      });

      // Since we're removing a payment from an unfinished sale we want to fully revert
      // the payment including the tipAmount
      payment.refundTip = true;

      // should be new terminals that utilize the payment service so make sure we have a payment ID
      if (payment.isServicePayment()) {
        const tipAmount = payment.model.get('tipAmount') || null;
        const cardType = get(payment, 'model.details.cardType');

        if (cardType === PaymentTransaction.SupportedCardType.INTERAC) {
          const refundableAmount = payment.model.get('amount').negated();
          const refundedPaymentID = payment.model.get('paymentID');
          const terminal = this.getTerminal();

          if (refundableAmount && refundedPaymentID && terminal) {
            if (this._paymentProvider.isGatewayLightspeedPayments && terminal.hasMonitoringModule) {
              terminal.startLspayMonitoring(this._paymentProvider.activeGatewayPli, {
                cardType,
                isRefund: true,
              });
            }

            return TerminalPaymentModal.doTerminalPayment({
              initialAmount: refundableAmount,
              terminal,
              saleManager: this._saleManager,
              paymentProvider: this._paymentProvider,
              isManualEntry: false,
              isUnreferencedRefund: false,
              refundedPaymentID,
              ...(tipAmount && { tipAmount: tipAmount.negated() }),
            });
          }

          if (!refundableAmount || !refundedPaymentID) {
            alertUser(Localize.translate(`Unable to void payment. ${supportMessage}`));
          }

          if (!terminal) {
            alertUser(
              Localize.translate(
                `Unable to find the terminal used for this transaction. Finish this sale, then refund it.`
              )
            );
          }
        } else {
          return PaymentOperations.doVoidOperation(
            payment,
            this._saleManager,
            this._paymentProvider
          )
            .done((response) => {
              if (!RetailManager.active) {
                this._salePayments.remove(paymentView.model);
              }

              if (
                this._paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.VANTIV_TRIPOS ||
                this._paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
              ) {
                const message =
                  response.serviceModel &&
                  response.serviceModel.paymentTransaction &&
                  response.serviceModel.paymentTransaction.parameters &&
                  response.serviceModel.paymentTransaction.parameters.message
                    ? response.serviceModel.paymentTransaction.parameters.message
                    : response.error;

                const detailedMessage =
                  response.serviceModel &&
                  response.serviceModel.paymentTransaction &&
                  response.serviceModel.paymentTransaction.parameters &&
                  response.serviceModel.paymentTransaction.parameters.detailedMessage
                    ? response.serviceModel.paymentTransaction.parameters.detailedMessage
                    : null;
                const RenderedComponent = createReactRenderer({
                  component: TransactionResultModal,
                  selector: '[data-react-root="ReactModal"]',
                  props: {
                    onHide: () => {
                      delete this.TransactionResultModal;
                      RenderedComponent.unmount();
                    },
                    amount: response.model.get('amount'),
                    message,
                    detailedMessage,
                    paymentDetails: {
                      cardBrand: get(response, 'model.details.cardBrand', 'Unknown'),
                      maskedDigits: get(response, 'model.details.maskedDigits', ''),
                    },
                    salePaymentID: response.serviceModel
                      .get('SalePayment')
                      .salePaymentID.toString(),
                    status: Payment.Status.VOIDED,
                    systemCustomerID: window.merchantos.session.getSystemCustomerID(),
                  },
                });

                this.TransactionResultModal = RenderedComponent.mount();
              }
            })
            .fail(() => {
              alertUser(Localize.translate(`Unable to void payment. ${supportMessage}`));
            });
        }
      } else if (
        RetailManager.active &&
        this._paymentProvider.activeGatewaySupportedTerminals &&
        payment.model.transactionReferenceID
      ) {
        // The payment has a reference ID so it must be removed in a special way.
        // This can only happen in Retail Manager.
        RetailManager.cardReader.initiatePaymentTerminalRefund(
          this._saleManager.sale.id.toString(),
          payment.model.transactionReferenceID,
          payment.amount.toString(),
          this._saleManager.pendingBalance.toString()
        );
      } else {
        return this._saleManager
          .removePayment(payment)
          .done(() => {
            this._salePayments.remove(paymentView.model);
          })
          .fail((errorMessage) => {
            Monitoring.addError(
              new Error(`Merchant received an error while removing a payment from the sale`),
              'PaymentListViewController',
              {
                reason: errorMessage,
              }
            );

            alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
          });
      }
    } else if (payment instanceof GiftCardPayment || payment instanceof SwipePayment) {
      return this._saleManager
        .removePayment(payment)
        .done(() => {
          this._salePayments.remove(paymentView.model);
        })
        .fail((errorMessage) => {
          Monitoring.addError(
            new Error(
              `Merchant received an error while removing a giftcard or a swipe payment in a sale`
            ),
            'PaymentListViewController',
            {
              reason: errorMessage,
            }
          );

          alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
        });
    } else if (payment instanceof DepositPayment) {
      this._removeDepositRefund(payment, paymentView);
    } else if (payment instanceof CardOnFilePayment) {
      return PaymentOperations.doRemoveCardOnFilePaymentOperation(
        payment,
        this._saleManager,
        this._paymentProvider
      )
        .done((response) => {
          if (!RetailManager.active) {
            this._salePayments.remove(paymentView.model);
          }
        })
        .fail(() => {
          alertUser(Localize.translate(`Unable to void payment. ${supportMessage}`));
        });
    } else {
      // We reach this case when the payment type is no-longer accessible.
      // If the Payment Service goes down this could be "user defined" or
      // any other type that won't match any of the following types:
      // [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]
      Monitoring.addError(
        new Error(`Merchant received an error while removing a payment in a sale`),
        'PaymentListViewController',
        {
          reason: `The payment type "${payment.constructor.name}" is not an instance of [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]`,
        }
      );

      alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
    }
  }

  getTerminal() {
    return this._paymentProvider.getTerminal(CommonData.selectedRegister.get('ccTerminalID'));
  }

  /**
   * Event handler for when Retail Manager indicates that refund has been completed.
   *
   * @param {object} refundDetails
   * @param {string} refundDetails.transactionReferenceID   The original transaction reference ID
   * @param {string} [refundDetails.salePaymentID]  The ID of the original payment that was refunded
   * @private
   */
  _onRefundComplete(refundDetails) {
    let removedPayment = this._salePayments.find(
      (payment) =>
        (refundDetails.transactionReferenceID &&
          payment.transactionReferenceID === refundDetails.transactionReferenceID) ||
        (refundDetails.salePaymentID && payment.id === refundDetails.salePaymentID)
    );

    if (removedPayment) {
      let payment = PaymentFactory.createPaymentFromModel(removedPayment);

      this._saleManager
        .removePayment(payment)
        .done(() => {
          this._salePayments.remove(removedPayment);
        })
        .fail(() => {
          alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
        });
    } else {
      // It could be that the payment was removed with terminal interaction.  Find a matching reference.
      let servicePayment = this._saleManager.getServicePaymentByGatewayRef(
        refundDetails.transactionReferenceID
      );
      if (servicePayment) {
        removedPayment = this._salePayments.find(
          (payment) => payment.get('paymentID') === servicePayment.id
        );

        if (removedPayment) {
          this._salePayments.remove(removedPayment);
        }
      }
    }
  }

  /**
   * Adds any deposit refund payments to the payment list
   * @private
   */
  _addDepositRefunds() {
    // It's a good thing the payment UI is completely changing because this view needs a re-think.
    // Anyway, we're adding the deposit refunds to the list of payments so we can display them
    // in the payment list.
    let depositRefunds = this._saleManager.deposits.depositRefunds;
    _.each(depositRefunds, (depositRefund) => {
      depositRefund.model.depositPayment = depositRefund.depositPayment;
      this._salePayments.add(depositRefund.model);
    });
  }

  /**
   * Refreshes the credit payments with the most up-to-date data.
   *
   * @private
   */
  _refreshPayments() {
    this._salePayments.reset(this._saleManager.creditDebitPayments.models);
    this._salePayments.add(this._saleManager.giftCardPayments.models); // merge the collections

    this._addDepositRefunds();
  }

  /**
   * Event handler for when a sale has started the completion process
   */
  _onCompleteSaleStarted() {
    // force all remove (i.e. void) buttons to be disabled to prevent voiding while a sale is completing
    this.view.options.disableRemoveButton = true;
    this._refreshPayments();
  }

  /**
   * Event handler for when a sale has failed to complete
   */
  _onCompleteSaleFailed() {
    // re-enable all remove (i.e. void) buttons
    if (this.view.options.disableRemoveButton) {
      delete this.view.options.disableRemoveButton;
    }

    this._refreshPayments();
  }

  /**
   * Removes a deposit refund payment.
   * Conditionally updates or deletes the refund intent BEFORE removing from local state
   * if invoicing is enabled.
   *
   * @param {DepositPayment} payment The deposit refund payment to remove
   * @param {PaymentListPaymentView} paymentView The view of the payment
   * @private
   */
  async _removeDepositRefund(payment, paymentView) {
    // Update or delete refund intent BEFORE removing deposit refund from R-Series
    if (Modules.hasModule('invoicing.api.v1.refund_intent')) {
      const refundIntentSuccess = await this._updateOrDeleteRefundIntent(payment);
      if (!refundIntentSuccess) {
        alertUser(Localize.translate('Could not update refund intent. Please try again.'));
        return;
      }
    }

    // Remove deposit refund from local state
    this._saleManager.deposits.removeDepositRefund(payment);
    this._salePayments.remove(paymentView.model);
  }

  /**
   * Updates or deletes the refund intent based on remaining deposit refunds.
   * This method is called BEFORE the deposit refund is removed from R-Series.
   * If there are remaining refunds (after removing this one), updates the refund intent with the new total.
   * If there are no more refunds (after removing this one), deletes the refund intent.
   * Also deletes invoice intent and deposit usage intent when no refunds remain.
   *
   * @param {DepositPayment} paymentBeingRemoved The deposit refund payment being removed
   * @returns {Promise<boolean>} True if the operation was successful, false otherwise
   * @private
   */
  async _updateOrDeleteRefundIntent(paymentBeingRemoved) {
    const customer = this._saleManager.customer;
    const creditAccountId = customer?.get('creditAccountID');
    const customerId = customer?.id;
    const saleId = this._saleManager.sale?.id;
    const employee = window.merchantos.session.getEmployee();
    // Calculate total refund amount AFTER removing this payment
    const currentRefundTotal = this._saleManager.deposits.refundTotal;
    const removedAmount = paymentBeingRemoved.amount.abs();
    const newRefundTotal = currentRefundTotal.minus(removedAmount);

    if (!saleId || !creditAccountId) {
      return true; // No sale or credit account, continue with flow
    }

    try {
      if (newRefundTotal.isZero()) {
        // No more refunds - delete all intents (refund, invoice, deposit usage)
        await RefundIntentService.deleteRefundIntent(creditAccountId, `${saleId}`);
      } else {
        // Still has refunds - update the refund intent with new total
        const registerID = this._saleManager.sale?.get('registerID');
        const currency = window.merchantos.locale.currency_code;
        const shopData = await InvoiceServiceLegacy.getLocationData();

        if (!shopData) {
          datadogRum.addError(new Error('Location data not available'), {
            message: 'Failed to get location data for refund intent update',
            saleId,
          });
          return false;
        }

        await RefundIntentService.upsertRefundIntent({
          creditAccountId,
          customerId: `${customerId}`,
          saleId: `${saleId}`,
          amount: newRefundTotal.toNumber(),
          currency,
          accountId: String(window.merchantos.session.getSystemCustomerID()),
          registerRef: `${registerID}`,
          invoice: {
            customer: buildCustomerFromData(customer?.attributes),
            employee: buildEmployeeFromData(
              employee
                ? {
                    employeeID: employee.employeeID,
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                    email: getEmployeeEmail(employee),
                  }
                : undefined
            ),
            location: buildLocationFromShopData(shopData),
          },
        });
      }
      return true;
    } catch (error) {
      // Log error and block the deposit refund removal flow
      datadogRum.addError(error, {
        message: 'Failed to update or delete refund intent during deposit refund removal',
        saleId,
        creditAccountId,
        remainingAmount: newRefundTotal.toString(),
      });
      return false;
    }
  }
}
